import { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Stack, Modal } from 'react-bootstrap';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

import { SkillsScoringOptionsService } from '@api/SkillsScoringOptionsService';
import {
  useAddSkillsScoringOptions,
  useUpdateSkillsScoringOptions,
  useDeleteSkillsScoringOptions,
  useStatusChangeSkillsScoringOptions,
  useViewSkillScoringParameters,
  useRouteAccessList
} from '@hooks';

import { getStoredDataUsingKey, setAllStoredData, removeStoredData } from '../../../../helpers/localStorageHelper';
import CommonSEOTitle from '../../../../helpers/CommonSEOTitle';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Assets
import CloseIconPopup from '../../../../assets/images/svg/CloseIcon.js';
import AddIcon from '../../../../assets/images/svg/add.svg';
import editIcon from '../../../../assets/images/svg/table-edit.svg';
import deleteIcon from '../../../../assets/images/svg/table-delete.svg';
import { TNButton, TNTable } from '@common/components';

import validationSchema from './SkillsScoringOptionsValidation';

const ListSkillsScoringOptionsPage = ({ t }) => {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const storageKey = `adminSkillsScoringOptions`;
  const searchDebounceTimeout = useRef(null);

  // State
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(getStoredDataUsingKey(storageKey, 'currentPage', 1));
  const [searchText, setSearchText] = useState(getStoredDataUsingKey(storageKey, 'searchText', ''));
  const [searchValue, setSearchValue] = useState(searchText);
  const [paginationData, setPaginationData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(getStoredDataUsingKey(storageKey, 'selectedStatus', ''));
  const [routeName, setRouteNames] = useState([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Fetch skill details
  const { data: skillData, isLoading: isSkillLoading, isError, error } =
    useViewSkillScoringParameters(skillId, () => { }, () => { });

  useEffect(() => {
    if (isError && error?.response?.status === 404) {
      navigate('/404');
    }
  }, [isError, error, navigate]);
  
  // Fetch list function
  const fetchData = async () => {
    try {
      const res = await SkillsScoringOptionsService.listSkillsScoringOptions({
        page: currentPage,
        searchText,
        status: selectedStatus,
        skillId,
      });
      setData(res.data.skills_scoring_options_list);
      setPaginationData(res.data.pagination);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Initial fetch & on filter/pagination change
  useEffect(() => {
    fetchData();
  }, [currentPage, searchText, selectedStatus, skillId]);

  // Persist filters
  useEffect(() => {
    setAllStoredData(storageKey, { searchText, currentPage, selectedStatus }, false);
  }, [currentPage, searchText, selectedStatus]);

  // Formik
  const formik = useFormik({
    initialValues: { option_name: '', option_note: '', option_status: '', option_color: '' },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = { ...values, skill_scoring_parameters_id: skillId };
      if (isEditMode) doUpdate({ id: editId, ...payload });
      else doAdd(payload);
    }
  });

  // API Mutations
  const { mutate: doAdd, isLoading: isAdding } = useAddSkillsScoringOptions(
    (res) => { toast.success(res.message); fetchData(); setShowModal(false); formik.resetForm(); },
    (err) => {
      if (err?.errors) Object.entries(err.errors).forEach(([k, v]) => formik.setFieldError(k, v[0]));
      toast.error(err.message);
    }
  );

  const { mutate: doUpdate, isLoading: isUpdating } = useUpdateSkillsScoringOptions(
    (res) => { toast.success(res.message); fetchData(); setShowModal(false); },
    (err) => {
      if (err?.errors) Object.entries(err.errors).forEach(([k, v]) => formik.setFieldError(k, v[0]));
      toast.error(err.message);
    }
  );

  const { mutate: doDelete } = useDeleteSkillsScoringOptions(
    (res) => { toast.success(res.message); fetchData(); },
    (err) => toast.error(err.message)
  );

  const { mutate: doUpdateStatus } = useStatusChangeSkillsScoringOptions(
    (res) => { toast.success(res.message); fetchData(); },
    (err) => toast.error(err.message)
  );

  // Route access
  useRouteAccessList((res) => setRouteNames(res.data.route_name));

  // Handlers
  const handleModalOpen = (editData = null) => {
    if (editData) {
      setIsEditMode(true);
      setEditId(editData.skills_scoring_options_id);
      formik.setValues({
        option_name: editData.option_name || '',
        option_note: editData.option_note || '',
        option_status: editData.option_status || '',
        option_color: editData.option_color || ''
      });
    } else {
      setIsEditMode(false);
      setEditId(null);
      formik.resetForm();
    }
    setShowModal(true);
  };

  const handleDeleteClick = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
          <div className="alert-popup">
            <h2>{t('page.skills_scoring_options_delete_alert')}</h2>
            <TNButton className="table-delete-button" onClick={() => { doDelete({ id }); onClose(); }}>
              {t('page.alert_popup_yes_button')}
            </TNButton>
            <TNButton className="table-primary-button" onClick={onClose}>
              {t('page.alert_popup_no_button')}
            </TNButton>
          </div>
        </div>
      )
    });
  };

  const handleStatusClick = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
          <div className="alert-popup">
            <h2>{t('page.skill_scoring_options_alert_popup_message')}</h2>
            <TNButton className="table-delete-button" onClick={() => { doUpdateStatus({ id }); onClose(); }}>
              {t('page.alert_popup_yes_button')}
            </TNButton>
            <TNButton className="table-primary-button" onClick={onClose}>
              {t('page.alert_popup_no_button')}
            </TNButton>
          </div>
        </div>
      )
    });
  };

  const shouldShowRoute = (route) => routeName.includes(route);

  // Columns
  const actionColumn = {
    Header: t('page.skill_score_options_status_action'),
    accessor: 'skills_scoring_options_id',
    Cell: ({ row }) => {
      const record = row.original;
      return (
        <>
          <div className="d-flex" style={{ gap: '12px' }}>
            {shouldShowRoute('skills-scoring-options.update') && (
              <TNButton className="table-button edit-btn" onClick={() => handleModalOpen(record)}>
                <img src={`admin/${editIcon}`} />
              </TNButton>
            )}
            {shouldShowRoute('skills-scoring-options.destroy') && (
              <TNButton className="table-button delete-btn" onClick={() => handleDeleteClick(record.skills_scoring_options_id)}>
                <img src={`admin/${deleteIcon}`} />
              </TNButton>
            )}
            
            {shouldShowRoute('skills-scoring-options.index') && (
              <span
                id={row.original.skills_scoring_options_id}
                style={{ cursor: 'pointer' }}
                className={`status-label ${row.original.option_status == '1' ? 'status-label green' : 'status-label red'}`}
                onClick={() => handleStatusClick(row.original.skills_scoring_options_id)}
              >
                {row.original.option_status == '1' ? 'Active' : 'Inactive'}
              </span>
            )}


          </div>
        </>
      );
    },
    disableSortBy: true
  };

  const columnsjson = [
    { Header: t('page.skills_scoring_options_create_at'), accessor: 'created_at', disableSortBy: true },
    {
      Header: t('page.skills_scoring_options_name'),
      accessor: 'option_name',
      disableSortBy: true,
      Cell: ({ row }) => {
        let colorClass = '';

        switch (row.original.option_color) {
          case 1: // Green
            colorClass = 'status-label taskgreen capitalize';
            break;
          case 2: // Blue
            colorClass = 'status-label taskblue capitalize';
            break;
          case 3: // Red
            colorClass = 'status-label bubble-red capitalize';
            break;
          default:
            colorClass = 'status-label taskblue capitalize'; // default fallback
        }

        return <span className={colorClass}>{row.original.option_name}</span>;
      }
    },
    {
      Header: t('page.skills_scoring_options_note'),
      accessor: 'option_note',
      disableSortBy: true,
      Cell: ({ row }) => {
        const content = row.original?.option_note ?? '-';
        const [expanded, setExpanded] = useState(false);
        const toggleExpand = () => setExpanded(prev => !prev);
        if (!row.original.option_note) return <p className="text-start m-0">-</p>;
        if (content.length <= 100) return content;
        return (
          <div>
            <p dangerouslySetInnerHTML={{ __html: expanded ? content : content.substring(0, 50) + '...' }} className="m-0" style={{maxWidth: '300px', whiteSpace: 'break-spaces' }} />
            <span onClick={toggleExpand} className="btn btn-link p-0" style={{ fontSize: '0.85rem' }} >
              {expanded ? 'Read less' : 'Read more'}
            </span>
          </div>
        );
      }
    }
  ];

  const showActionColumn = shouldShowRoute('skills-scoring-options.update') || shouldShowRoute('skills-scoring-options.destroy') || shouldShowRoute('skills-scoring-options.index');
  if (showActionColumn) columnsjson.push(actionColumn);

  const columns = useMemo(() => columnsjson, [routeName]);

  const pageIndexHandle = (pageIndex) => setCurrentPage(pageIndex + 1);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (searchDebounceTimeout.current) clearTimeout(searchDebounceTimeout.current);
    searchDebounceTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      setSearchText(value);
    }, 500);
  };

  const handleClearFilter = () => {
    setSearchValue('');
    setSearchText('');
    setCurrentPage(1);
    setSelectedStatus('');
    removeStoredData(storageKey);
  };

  const optionStatusOptions = [
    { value: 1, label: t('page.active_status_name') },
    { value: 2, label: t('page.in_active_status_name') },
  ];

  if (!paginationData) return null;

  return (
    <>
      <CommonSEOTitle title={t('page.skills_scoring_options_list_label')} />
      <div className="page-title d-flex align-items-center gap-2 flex-wrap">
        <h1 className="title d-flex align-items-center gap-2 flex-wrap">
          {t('page.skills_scoring_options_list_label')}
          {!isSkillLoading && skillData?.data?.skill_name && (
            <span className="status-label blue bordered mt-1 mt-md-0 capitalize" style={{whiteSpace: 'break-spaces', padding:'4px 10px'}}>
              <span className="me-2">•</span>
              {skillData.data.skill_name}
            </span>
          )}
        </h1>

        {shouldShowRoute('skills-scoring-options.store') && (
          <TNButton className="table-add-button new ms-md-auto" type="button" onClick={() => handleModalOpen()}>
            <img src={`admin/${AddIcon}`} /> {t('page.add_skills_scoring_options_label')}
          </TNButton>
        )}
      </div>

      <TNTable
        columns={columns}
        data={data}
        paginationData={paginationData}
        onSelectPage={pageIndexHandle}
        t={t}
        pageIndexGet={currentPage - 1}
        key={Math.floor(Math.random() * 1000)}
      />

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" className='add-hr-score-popup'>
        <Modal.Header>
          {isEditMode ? t('page.edit_skills_scoring_options_label') : t('page.add_skills_scoring_options_label')}
          <Button className='close-popup-btn' onClick={() => setShowModal(false)}><CloseIconPopup /></Button>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-4">
              <div className='d-flex align-items-center justify-content-between'>
                <Form.Label className="control-label">Select Option Color</Form.Label>
                <div className="d-flex gap-3 align-items-center">
                  {[
                    { value: 1, colorClass: 'green' },
                    { value: 2, colorClass: 'blue' },
                    { value: 3, colorClass: 'red' },
                  ].map((option) => (
                    <div
                      key={option.value}
                      className={`score-option ${option.colorClass} ${formik.values.option_color === option.value ? 'active' : ''
                        }`}
                      onClick={() => {
                        formik.setFieldValue('option_color', option.value);
                        formik.setFieldTouched('option_color', true, false);
                      }}
                    ></div>
                  ))}
                </div>
              </div>
              {(formik.touched.option_color || formik.submitCount > 0) && formik.errors.option_color && (
                <div className="form-field-error-text">{t(formik.errors.option_color)}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                name="option_name"
                placeholder="Enter Option Name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.option_name}
                className={formik.touched.option_name && formik.errors.option_name
                  ? 'form-field-error input-control'
                  : 'input-control'}
              />
              {formik.touched.option_name && formik.errors.option_name && (
                <div className="form-field-error-text">{t(formik.errors.option_name)}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Select
                classNamePrefix="add-page-select"
                name="option_status"
                placeholder="Option Status"
                options={optionStatusOptions}
                value={
                  formik.values.option_status
                    ? optionStatusOptions.find(
                      (opt) => opt.value === formik.values.option_status
                    )
                    : null
                }
                onChange={(selectedOption) => {
                  formik.setFieldValue(
                    'option_status',
                    selectedOption ? selectedOption.value : ''
                  );
                }}
                onBlur={formik.handleBlur}
                className={
                  formik.touched.option_status && formik.errors.option_status
                    ? 'form-select-error'
                    : formik.touched.option_status && !formik.errors.option_status
                      ? 'form-select-success'
                      : ''
                }
              />
              {formik.touched.option_status && formik.errors.option_status && (
                <div className="form-field-error-text">
                  {t(formik.errors.option_status)}
                </div>
              )}
            </Form.Group>


            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="option_note"
                placeholder="Enter Option Note"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.option_note}
                className={formik.touched.option_note && formik.errors.option_note
                  ? 'form-field-error input-control'
                  : 'input-control'}
              />
              {formik.touched.option_note && formik.errors.option_note && (
                <div className="form-field-error-text">{t(formik.errors.option_note)}</div>
              )}
            </Form.Group>

            <TNButton type="submit" loading={isAdding || isUpdating} className="popup-add-btn">
              {isEditMode ? 'Update' : 'Add'}
            </TNButton>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

ListSkillsScoringOptionsPage.propTypes = { t: PropTypes.func };
export default ListSkillsScoringOptionsPage;
